import { supabase } from "./supabase";

// ── Clients ────────────────────────────────────────────────────
export const fetchClients = async () => {
  const { data, error } = await supabase
    .from("clients")
    .select("*, contacts(*), engagement_letters(*)")
    .order("name");
  if (error) throw dbErr(error);
  return data.map(normaliseClient);
};

export const createClient = async (client) => {
  // eslint-disable-next-line no-unused-vars
  const {
    contacts, engagement_letters, engagementLetters, services,
    tasks, deadlines, overdue, id: _id,
    // contactName/Email/Phone are form-only fields — saved to the contacts table below, not as columns on clients
    contactName, contactEmail, contactPhone,
    ...rest
  } = client;
  let payload = toSnake(rest);
  // Retry loop: strip any columns Supabase rejects so a schema mismatch never blocks client creation
  let data, error;
  for (let attempt = 0; attempt < 10; attempt++) {
    ({ data, error } = await supabase.from("clients").insert(payload).select().single());
    if (!error) break;
    const colMatch = error.message?.match(/['"]([\w]+)['"]\s+column of ['"]?clients['"]?/)
      || error.message?.match(/column ['"]?([\w]+)['"]? of ['"]?clients['"]?/);
    if (colMatch) { delete payload[colMatch[1]]; } else { break; }
  }
  if (error) throw dbErr(error);
  if (contacts && contacts.length > 0) {
    await supabase.from("contacts").insert(
      contacts.map((c) => ({ client_id: data.id, name: c.name, email: c.email, phone: c.phone, primary_contact: c.primary || false }))
    );
  }
  const created = normaliseClient(data);
  created.contacts = contacts || [];
  return created;
};

// Dedicated services writer — bypasses the updateClient retry loop.
// Writes services directly to the client row so every device gets them via fetchClients.
export const setClientServices = async (id, services) => {
  const { error } = await supabase
    .from("clients")
    .update({ services })
    .eq("id", id);
  if (error) throw error;
};

export const deleteClient = async (id) => {
  const sid = String(id);
  const tryDel = async (table, col, val) => {
    try { await supabase.from(table).delete().eq(col, val ?? id); } catch {}
  };

  await Promise.all([
    tryDel("tasks",           "client_id"),
    tryDel("deadlines",       "client_id"),
    tryDel("notes",           "client_id"),
    tryDel("communications",  "client_id"),
    tryDel("contacts",        "client_id"),
    tryDel("portal_messages", "client_id"),
    tryDel("signing_requests","client_id", sid),
  ]);

  // Documents: remove from storage then DB
  try {
    const { data: docs } = await supabase.from("documents").select("file_path").eq("client_id", id);
    if (docs?.length > 0) {
      const paths = docs.map((d) => d.file_path).filter(Boolean);
      if (paths.length) {
        try { await supabase.storage.from("portal-attachments").remove(paths); } catch {}
        try { await supabase.storage.from("documents").remove(paths); } catch {}
      }
      await tryDel("documents", "client_id");
    }
  } catch {}

  // Delete per-client app_settings blobs
  const appKeys = [
    `client_onboarding_${id}`, `client_docchecklist_${id}`,
    `client_portal_${id}`, `cod_requests_${id}`,
    `client_data_${id}`, `client_workpapers_${id}`,
  ];
  await Promise.all(appKeys.map(k => tryDel("app_settings", "key", k)));

  // Finally delete the client row itself
  const { error } = await supabase.from("clients").delete().eq("id", id);
  if (error) throw dbErr(error);
};

export const updateClient = async (id, patch) => {
  // eslint-disable-next-line no-unused-vars
  const { contacts, engagement_letters, engagementLetters, tasks, deadlines, overdue, notes, id: _id, onboardingState, complianceCustom, docChecklist, piScoreData, ...rest } = patch;
  // Retry loop: if Supabase rejects a column that doesn't exist in the schema, strip it and retry
  let payload = toSnake(rest);
  // JSON columns stored directly on the clients row — add them explicitly
  if (onboardingState !== undefined) payload.onboarding_state = onboardingState;
  if (docChecklist     !== undefined) payload.doc_checklist   = docChecklist;
  if (complianceCustom !== undefined) payload.compliance_custom = complianceCustom;
  if (piScoreData      !== undefined) payload.pi_score_data   = piScoreData;
  let data, error;
  for (let attempt = 0; attempt < 10; attempt++) {
    ({ data, error } = await supabase.from("clients").update(payload).eq("id", id).select().maybeSingle());
    if (!error) break;
    const colMatch = error.message?.match(/['"]([\w]+)['"]\s+column of ['"]?clients['"]?/)
      || error.message?.match(/column ['"]?([\w]+)['"]? of ['"]?clients['"]?/);
    if (colMatch) { delete payload[colMatch[1]]; } else { break; }
  }
  if (error) throw dbErr(error);
  // If contacts were passed, replace them
  if (contacts !== undefined) {
    await supabase.from("contacts").delete().eq("client_id", id);
    if (contacts.length > 0) {
      await supabase.from("contacts").insert(
        contacts.map((c) => ({ client_id: id, name: c.name, email: c.email, phone: c.phone, primary_contact: c.primary || false }))
      );
    }
  }
  const saved = data ? normaliseClient(data) : { id };
  saved.contacts = contacts !== undefined ? contacts : saved.contacts;
  return saved;
};

// ── Tasks ──────────────────────────────────────────────────────
export const fetchTasks = async () => {
  const { data, error } = await supabase.from("tasks")
    .select("id, title, client_id, client, due, status, priority, assignee, description, checklist")
    .order("due");
  if (error) throw dbErr(error);
  return data.map(normaliseTask);
};

export const createTask = async (task) => {
  // eslint-disable-next-line no-unused-vars
  const { id: _id, clientName: _cn, dueDate: _dd, category: _cat, ...rest } = task;
  // Map dueDate → due, clientName → client if present in original task
  if (task.dueDate && !rest.due)        rest.due    = task.dueDate;
  if (task.clientName && !rest.client)  rest.client = task.clientName;
  // Normalise status: "Open" is not a valid DB value
  if (!rest.status || rest.status === "Open") rest.status = "To Do";
  const row = toSnake(rest);
  let data, error;
  for (let attempt = 0; attempt < 8; attempt++) {
    ({ data, error } = await supabase.from("tasks").insert(row).select().single());
    if (!error) break;
    const m = error.message?.match(/['"]([\w]+)['"]\s+column of ['"]?tasks['"]?/)
           || error.message?.match(/column ['"]?([\w]+)['"]? of ['"]?tasks['"]?/);
    if (m) { delete row[m[1]]; } else { break; }
  }
  if (error) throw dbErr(error);
  return normaliseTask(data);
};

export const updateTask = async (id, patch) => {
  // eslint-disable-next-line no-unused-vars
  const { id: _id, ...rest } = patch;
  const { data, error } = await supabase.from("tasks").update(toSnake(rest)).eq("id", id).select().single();
  if (error) throw dbErr(error);
  return normaliseTask(data);
};

export const deleteTask = async (id) => {
  const { error } = await supabase.from("tasks").delete().eq("id", id);
  if (error) throw dbErr(error);
};

// ── Deadlines ──────────────────────────────────────────────────
export const fetchDeadlines = async () => {
  const { data, error } = await supabase.from("deadlines")
    .select("id, title, client_id, client, due, reminder_date, freq, status, recurring")
    .order("due");
  if (error) throw dbErr(error);
  return data.map(normaliseDeadline);
};

export const createDeadline = async (dl) => {
  // Strip app-only fields; map camelCase fields that differ from DB column names
  const { reminderDate, recurring, category, description, clientName, dueDate, ...rest } = dl;
  const row = toSnake(rest);
  if (clientName && !row.client) row.client = clientName;
  if (dueDate && !row.due)       row.due    = dueDate;
  if (recurring !== undefined)   row.recurring     = !!recurring;
  if (reminderDate)              row.reminder_date = reminderDate;
  // Retry loop: strip any columns Supabase rejects so a schema mismatch never blocks creation
  let data, error;
  for (let attempt = 0; attempt < 8; attempt++) {
    ({ data, error } = await supabase.from("deadlines").insert(row).select().single());
    if (!error) break;
    const m = error.message?.match(/['"]([\w]+)['"]\s+column of ['"]?deadlines['"]?/)
           || error.message?.match(/column ['"]?([\w]+)['"]? of ['"]?deadlines['"]?/);
    if (m) { delete row[m[1]]; } else { break; }
  }
  if (error) throw error;
  const result = normaliseDeadline(data);
  result.reminderDate = reminderDate || "";
  result.recurring    = recurring ?? true;
  return result;
};

export const updateDeadline = async (id, patch) => {
  // eslint-disable-next-line no-unused-vars
  const { reminderDate, recurring, id: _id, ...rest } = patch;
  const row = toSnake(rest);
  if (recurring !== undefined) row.recurring = !!recurring;
  if (reminderDate) row.reminder_date = reminderDate;
  const { data, error } = await supabase.from("deadlines").update(row).eq("id", id).select().single();
  if (error) throw error;
  const result = normaliseDeadline(data);
  result.reminderDate = reminderDate || data.reminder_date || "";
  result.recurring    = recurring ?? data.recurring ?? true;
  return result;
};

export const deleteDeadline = async (id) => {
  const { error } = await supabase.from("deadlines").delete().eq("id", id);
  if (error) throw error;
};

// ── Communications ─────────────────────────────────────────────
export const fetchComms = async () => {
  const { data, error } = await supabase.from("communications")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw dbErr(error);
  return data.map(normaliseComm);
};

export const fetchPortalMsgs = async (clientId) => {
  const { data } = await supabase.from("portal_messages").select("*")
    .eq("client_id", clientId).order("created_at", { ascending: false });
  return data || [];
};

export const sendPortalMessage = async ({ clientId, content, category }) => {
  const payload = { client_id: clientId, from_role: "practice", content };
  // Try with category first; fall back without it if column doesn't exist yet
  const { data, error } = await supabase.from("portal_messages")
    .insert({ ...payload, category: category !== "general" ? category : null })
    .select().single();
  if (error) {
    if (error.message?.includes("category") || error.message?.includes("42703")) {
      const { data: d2, error: e2 } = await supabase.from("portal_messages")
        .insert(payload).select().single();
      if (e2) throw e2;
      return d2;
    }
    throw error;
  }
  return data;
};

export const createComm = async (comm) => {
  const { data, error } = await supabase.from("communications").insert(toSnake(comm)).select().single();
  if (error) throw error;
  return normaliseComm(data);
};

export const deleteComm = async (id) => {
  const { error } = await supabase.from("communications").delete().eq("id", id);
  if (error) throw error;
};

export const updateComm = async (id, patch) => {
  const { data, error } = await supabase.from("communications").update(toSnake(patch)).eq("id", id).select().single();
  if (error) throw error;
  return normaliseComm(data);
};

// ── Notes ──────────────────────────────────────────────────────
export const fetchNotes = async () => {
  const { data, error } = await supabase.from("notes")
    .select("id, subject, body, date, client_id, client, status")
    .order("date", { ascending: false })
    .order("id", { ascending: false });
  if (error) throw dbErr(error);
  return data.map(normaliseNote);
};

export const createNote = async (note) => {
  const { data, error } = await supabase.from("notes").insert(toSnake(note)).select().single();
  if (error) throw error;
  return normaliseNote(data);
};

export const updateNote = async (id, patch) => {
  const { data, error } = await supabase.from("notes").update(toSnake(patch)).eq("id", id).select().single();
  if (error) throw error;
  return normaliseNote(data);
};

export const deleteNote = async (id) => {
  const { error } = await supabase.from("notes").delete().eq("id", id);
  if (error) throw error;
};

// ── Leads ──────────────────────────────────────────────────────
export const fetchLeads = async () => {
  const { data, error } = await supabase.from("leads").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data;
};

export const createLead = async (lead) => {
  const { data, error } = await supabase.from("leads").insert(lead).select().single();
  if (error) throw error;
  return data;
};

export const updateLead = async (id, patch) => {
  const { data, error } = await supabase.from("leads").update(patch).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

export const deleteLead = async (id) => {
  const { error } = await supabase.from("leads").delete().eq("id", id);
  if (error) throw error;
};

// ── App Settings (team, service labels, practice info, todos, templates) ──────
// ── Per-client JSONB blobs stored in app_settings ───────────────
// These bypass the clients table retry loop that silently strips unknown columns.
export const fetchClientOnboarding   = (id) => fetchAppSetting(`client_onboarding_${id}`);
export const saveClientOnboarding    = (id, state) => upsertAppSetting(`client_onboarding_${id}`, state);
export const fetchClientDocChecklist = (id) => fetchAppSetting(`client_docchecklist_${id}`);
export const saveClientDocChecklist  = (id, data)  => upsertAppSetting(`client_docchecklist_${id}`, data);

export const fetchAppSetting = async (key) => {
  // Use limit(1) + array result instead of maybeSingle() — maybeSingle() throws if
  // duplicate rows exist (possible when onConflict constraint is missing on app_settings.key).
  const { data, error } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", key)
    .order("updated_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0]?.value ?? null;
};

// ── Client Logos — dedicated Supabase key, completely separate from workpapers ─
// Logo URLs are small (just an https:// string) and must never be lost due to
// workpapers save failures. Stored as { clientName: logoUrl } in app_settings.
export const fetchClientLogos = () => fetchAppSetting("client_logos");
export const saveClientLogos = (logos) => upsertAppSetting("client_logos", logos);

// Fetch all workpaper chunks: the legacy full blob ("workpapers") plus any per-client
// rows saved as "workpapers_client:<clientName>". Per-client rows take priority.
export const fetchAllWorkpaperChunks = async () => {
  // Order by updated_at DESC so newest row wins when duplicate keys exist (legacy bug).
  const { data, error } = await supabase
    .from("app_settings")
    .select("key, value, updated_at")
    .like("key", "workpapers%")
    .order("updated_at", { ascending: false });
  if (error || !data) return null;
  let merged = {};
  // Legacy full blob — use only as fallback base; per-client rows overwrite below.
  const fullBackup = data.find(r => r.key === "workpapers");
  if (fullBackup?.value && typeof fullBackup.value === "object") {
    merged = { ...fullBackup.value };
  }
  // Per-client rows: since rows are ordered newest-first, skip a clientName once seen.
  const seenClients = new Set();
  data.forEach(row => {
    if (!row.key.startsWith("workpapers_client:")) return;
    const clientName = row.key.slice("workpapers_client:".length);
    if (seenClients.has(clientName)) return; // older duplicate — skip
    seenClients.add(clientName);
    if (row.value && typeof row.value === "object") {
      merged[clientName] = row.value;
    }
  });
  return Object.keys(merged).length > 0 ? merged : null;
};

export const upsertAppSetting = async (key, value) => {
  const { error } = await supabase
    .from("app_settings")
    .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
  if (error) throw error;
};

// Only fetch the keys needed at boot — avoids loading portal_bank_deposits_* etc. (54MB+)
const BOOT_SETTINGS_KEYS = [
  "clientServices", "team", "todos", "serviceLabels", "practiceSettings",
  "appToggles", "calendarEvents", "pinnedClientIds", "pinnedMap",
  "email_address_book", "client_logos", "google_oauth_tokens",
  "workpapers",
];

export const fetchAllAppSettings = async () => {
  const { data, error } = await supabase
    .from("app_settings")
    .select("key, value")
    .in("key", BOOT_SETTINGS_KEYS);
  if (error) throw error;
  const map = {};
  (data || []).forEach((r) => { map[r.key] = r.value; });
  return map;
};

// ── Checklist ──────────────────────────────────────────────────
export const fetchChecklist = async () => {
  const { data, error } = await supabase.from("checklist_state").select("item_key, checked");
  if (error) throw error;
  const map = {};
  (data || []).forEach((row) => { map[row.item_key] = row.checked; });
  return map;
};

export const toggleChecklistItem = async (key, checked) => {
  const { error } = await supabase
    .from("checklist_state")
    .upsert({ item_key: key, checked, updated_at: new Date().toISOString() }, { onConflict: "item_key" });
  if (error) throw error;
};

// ── Engagement letters ─────────────────────────────────────────
export const fetchEngagementLetters = async () => {
  const { data, error } = await supabase.from("engagement_letters").select("id, client_id, status, date");
  if (error) throw error;
  return data.map((r) => ({ id: r.id, clientId: r.client_id, status: r.status, date: r.date }));
};

export const upsertEngagementLetter = async (clientId, status, date) => {
  const { error } = await supabase
    .from("engagement_letters")
    .upsert({ client_id: clientId, status, date }, { onConflict: "client_id" });
  if (error) throw error;
};

// ── Documents ──────────────────────────────────────────────────
const BUCKET = "documents";

export const fetchDocuments = async ({ category, clientId } = {}) => {
  let q = supabase.from("documents").select("id, name, category, file_path, file_size, file_type, client_id, uploaded_at, uploaded_by").order("uploaded_at", { ascending: false });
  if (category) q = q.eq("category", safeStorageKey(category));
  if (clientId) q = q.eq("client_id", clientId);
  const { data, error } = await q;
  if (error) throw error;
  return data.map(normaliseDocument);
};

// Converts an arbitrary string to a Supabase-safe storage path segment.
// Supabase Storage rejects keys with ::, em-dashes, unencoded spaces in path
// components, and other special characters. This is applied to the category
// before building the filePath so both upload and fetch use the same key.
export function safeStorageKey(s) {
  return String(s)
    .replace(/[–—]/g, "-")        // em/en dash → hyphen
    .replace(/\s*\/\s*/g, "-")    // space/slash/space → hyphen (e.g. "FICA / KYC")
    .replace(/:{2,}/g, "-")       // :: separator → hyphen
    .replace(/[^a-zA-Z0-9._\-]/g, "_") // anything else → underscore
    .replace(/_+/g, "_")          // collapse underscores
    .replace(/-+/g, "-")          // collapse hyphens
    .replace(/^[_\-]+|[_\-]+$/g, ""); // trim leading/trailing
}

export const uploadDocument = async (file, category, clientId = null) => {
  const safeCategory = safeStorageKey(category);
  const safeName = file.name.replace(/[^a-zA-Z0-9._\-() ]/g, "_");
  const filePath = `${safeCategory}/${Date.now()}_${safeName}`;
  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(filePath, file, {
    contentType: file.type,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;
  const row = {
    name:        file.name,
    category:    safeCategory,  // store sanitised key so fetchDocuments query matches
    file_path:   filePath,
    file_size:   file.size,
    file_type:   file.type || "",
    uploaded_by: "Monique",
    ...(clientId ? { client_id: clientId } : {}),
  };
  const { data, error: insertErr } = await supabase.from("documents").insert(row).select().single();
  if (insertErr) throw dbErr(insertErr);
  return normaliseDocument(data);
};

export const deleteDocument = async (id, filePath) => {
  await supabase.storage.from(BUCKET).remove([filePath]);
  const { error } = await supabase.from("documents").delete().eq("id", id);
  if (error) throw error;
};

export const renameDocument = async (id, newName) => {
  const { error } = await supabase.from("documents").update({ name: newName }).eq("id", id);
  if (error) throw error;
};

export const moveDocument = async (id, newCategory) => {
  const safeCategory = safeStorageKey(newCategory);
  const { error } = await supabase.from("documents").update({ category: safeCategory }).eq("id", id);
  if (error) throw error;
};

export const renameFolderDocs = async (oldCategory, newCategory) => {
  const oldKey = safeStorageKey(oldCategory);
  const newKey = safeStorageKey(newCategory);
  const { error } = await supabase.from("documents").update({ category: newKey }).eq("category", oldKey);
  if (error) throw error;
};

export const getDocumentSignedUrl = async (filePath, download = false) => {
  const opts = download ? { download: true } : {};
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(filePath, 3600, opts);
  if (error) throw error;
  return data.signedUrl;
};

// Refresh a signed URL for any Supabase storage bucket (e.g. "taxshop-docs")
export const getSignedUrlForBucket = async (bucket, filePath, download = false) => {
  const opts = download ? { download: true } : {};
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600, opts);
  if (error) throw error;
  return data.signedUrl;
};

// ── Client Logos (stored in documents bucket under client-logos/) ──────────────
export const uploadClientLogo = async (clientName, dataUri) => {
  const res = await fetch(dataUri);
  const blob = await res.blob();
  const safeName = clientName.replace(/[^a-zA-Z0-9._\-]/g, "_");
  const filePath = `client-logos/${safeName}.webp`;
  await supabase.storage.from("documents").remove([filePath]).catch(() => {});
  const { error: uploadErr } = await supabase.storage.from("documents").upload(filePath, blob, {
    contentType: "image/webp",
    upsert: true,
  });
  if (uploadErr) throw uploadErr;
  // 10-year signed URL — effectively permanent for a logo
  const { data, error: urlErr } = await supabase.storage.from("documents").createSignedUrl(filePath, 315360000);
  if (urlErr) throw urlErr;
  return data.signedUrl;
};

// ── Profile Pictures (stored in documents bucket under profile-pics/) ──────────
const PROFILE_BUCKET = "documents";

export const uploadProfilePic = async (name, dataUrl) => {
  // Convert base64 data URL to blob
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const safeName = name.replace(/[^a-zA-Z0-9._\-]/g, "_");
  const filePath = `profile-pics/${safeName}.jpg`;
  // Remove old file first (ignore error if not found)
  await supabase.storage.from(PROFILE_BUCKET).remove([filePath]).catch(() => {});
  const { error: uploadErr } = await supabase.storage.from(PROFILE_BUCKET).upload(filePath, blob, {
    contentType: "image/jpeg",
    upsert: true,
  });
  if (uploadErr) throw uploadErr;
  // Return a long-lived signed URL (10 years)
  const { data, error: urlErr } = await supabase.storage.from(PROFILE_BUCKET).createSignedUrl(filePath, 315360000);
  if (urlErr) throw urlErr;
  return data.signedUrl;
};

export const deleteProfilePic = async (name) => {
  const safeName = name.replace(/[^a-zA-Z0-9._\-]/g, "_");
  await supabase.storage.from(PROFILE_BUCKET).remove([`profile-pics/${safeName}.jpg`]).catch(() => {});
};

// Fetch a fresh 10-year signed URL for an existing profile pic from Supabase Storage.
// Returns the URL string, or null if the file doesn't exist.
export const fetchProfilePicUrl = async (name) => {
  if (!name) return null;
  const safeName = name.replace(/[^a-zA-Z0-9._\-]/g, "_");
  const { data, error } = await supabase.storage
    .from(PROFILE_BUCKET)
    .createSignedUrl(`profile-pics/${safeName}.jpg`, 315360000);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
};

// Upload a file to storage only (no documents table row) and return a long-lived signed URL
export const uploadAttachment = async (file, clientId) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._\-()]/g, "_");
  const filePath = `Attachments/${clientId || "general"}/${Date.now()}_${safeName}`;
  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(filePath, file, {
    contentType: file.type,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;
  // 1-year signed URL
  const { data, error: urlErr } = await supabase.storage.from(BUCKET).createSignedUrl(filePath, 31536000, { download: true });
  if (urlErr) throw urlErr;
  return { filePath, signedUrl: data.signedUrl };
};

function normaliseDocument(r) {
  return {
    id:         r.id,
    name:       r.name,
    category:   r.category,
    filePath:   r.file_path,
    fileSize:   r.file_size || 0,
    fileType:   r.file_type || "",
    clientId:   r.client_id || null,
    uploadedAt: r.uploaded_at,
    uploadedBy: r.uploaded_by || "Monique",
  };
}

// ── Normalisers (snake_case DB → camelCase app) ────────────────
function normaliseNote(r) {
  return {
    id:       r.id,
    subject:  r.subject,
    body:     r.body  || "",
    date:     r.date,
    clientId: r.client_id || null,
    client:   r.client || "",
    status:   r.status,
  };
}

function normaliseClient(r) {
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    status: r.status,
    idNo: r.id_no,
    itNo: r.it_no,
    vatNo: r.vat_no,
    payeNo: r.paye_no,
    uifNo: r.uif_no,
    coida: r.coida,
    customsNo: r.customs_no,
    sicCode: r.sic_code,
    taxTreatment: r.tax_treatment,
    registrationDate: r.registration_date,
    fyEnd: r.fy_end,
    piScore: r.pi_score,
    assignee: r.assignee,
    accountInfo: r.account_info,
    services: Array.isArray(r.services) ? r.services : (r.services ? [r.services] : []),
    notes: r.notes || "",
    clientSince: r.client_since,
    lastUpdated: r.last_updated,
    physical: r.physical,
    postal: r.postal,
    wipValue: r.wip_value,
    unbilledHrs: r.unbilled_hrs,
    billedYTD: r.billed_ytd,
    wipAge: r.wip_age,
    acceptanceData: r.acceptance_data || null,
    cipcData: r.cipc_data || null,
    docChecklist: r.doc_checklist || null,
    onboardingState: r.onboarding_state || null,
    piScoreData: r.pi_score_data || null,
    complianceCustom: r.compliance_custom || null,
    plannerEvents: r.planner_events || null,
    xeroRef: r.xero_ref || "",
    tasks: r.tasks || 0,
    deadlines: r.deadlines || 0,
    overdue: r.overdue || 0,
    contacts: (r.contacts || []).map((c) => ({
      name: c.name, email: c.email, phone: c.phone, primary: c.primary_contact,
    })),
    engagementLetters: r.engagement_letters || [],
  };
}

function normaliseTask(r) {
  return {
    id: r.id,
    title: r.title,
    clientId: r.client_id,
    client: r.client,
    due: r.due,
    status: r.status,
    priority: r.priority,
    assignee: r.assignee,
    description: r.description,
    checklist: Array.isArray(r.checklist) ? r.checklist : [],
  };
}

function normaliseDeadline(r) {
  return {
    id: r.id,
    title: r.title,
    clientId: r.client_id,
    client: r.client,
    due: r.due,
    reminderDate: r.reminder_date || "",
    freq: r.freq,
    status: r.status,
    recurring: r.recurring,
  };
}

function normaliseComm(r) {
  return {
    id: r.id,
    clientId: r.client_id,
    client: r.client || "",
    parentId: r.parent_id || null,
    type: r.type,
    note: r.note,
    date: r.date,
    user: r.user_name,
  };
}

// ── Supabase plain-object errors → real Error instances ───────
function dbErr(e) {
  const msg = e?.message || e?.details || e?.hint || JSON.stringify(e) || "Database error";
  return new Error(msg);
}

// ── camelCase → snake_case for writes ─────────────────────────
function toSnake(obj) {
  const map = {
    clientId: "client_id", parentId: "parent_id", idNo: "id_no", itNo: "it_no", vatNo: "vat_no", payeNo: "paye_no",
    uifNo: "uif_no", customsNo: "customs_no", sicCode: "sic_code",
    taxTreatment: "tax_treatment", registrationDate: "registration_date",
    fyEnd: "fy_end", piScore: "pi_score", accountInfo: "account_info",
    clientSince: "client_since", lastUpdated: "last_updated",
    wipValue: "wip_value", unbilledHrs: "unbilled_hrs",
    billedYTD: "billed_ytd", wipAge: "wip_age", userName: "user_name",
    acceptanceData: "acceptance_data",
    cipcData: "cipc_data",
    docChecklist: "doc_checklist",
    onboardingState: "onboarding_state",
    piScoreData: "pi_score_data",
    plannerEvents: "planner_events",
    reminderDate: "reminder_date",
    xeroRef: "xero_ref",
  };
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[map[k] || k] = v;
  }
  return out;
}
